import { BusinessCenter, LocationCity, LockClock, Money, People } from "@mui/icons-material";
import { Alert, Box, Button, Container, Divider, Paper, Slide, Snackbar, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import React, { PropsWithChildren, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router";
import { IssueStateView } from "../../../components/IssueStateView";
import { addCareerApplication, getCareerDetails } from "../../../services/careers";
import { analytics } from "../../../services/firebase";
import { isEmailValid } from "../../../utilities";
import { ROUTE_PARAMS, ROUTE_PATH } from "../../../utilities/navigation";
import { mapDataToUI } from "../../../utilities/careers";

/** The Career page of a position (TODO for now all taken) */
export const CareerPositionPage = () => {
    const { [ROUTE_PARAMS.jobID]: positionId } = useParams();
    const navigate = useNavigate();

    return <CareerPositionContainer positionId={positionId}
        navigateToPosition={(id) => navigate(ROUTE_PATH.careers.positionBuild(id))} />
}

interface ICareerPositionContainerProps {
    /** The position id to fetch and display */
    positionId?: string;
    /** Callback to navigate to another position or all position if not provided */
    navigateToPosition: (positionId?: string) => void;
}
/** Container which take care of fetching the data of the position job opportunity and display its content */
const CareerPositionContainer = ({ positionId, navigateToPosition }: ICareerPositionContainerProps) => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = useQuery(
        ['career', positionId],
        () => getCareerDetails(positionId || ''),
        {
            enabled: !!positionId
        });

    if (isLoading) {
        return null;
    }

    if (!positionId || isError || !data || !data.is_published) {
        return <CareerPositionNotFoundScreen onActionClick={() => navigateToPosition()}>
            <CandidateApplication />
        </CareerPositionNotFoundScreen>
    }

    if (!!data?.is_pending_offer) {
        return <CareerPositionPendingOfferScreen onActionClick={() => navigateToPosition()}>
            <CandidateApplication />
        </CareerPositionPendingOfferScreen>
    }

    return <CareerPositionScreen
        {...mapDataToUI(data, t)}
    >
        <CandidateApplication positionId={positionId} />
    </CareerPositionScreen>
}

type ICareerPositionScreenProps<T> = PropsWithChildren<T> & {
    /** The main action in the screen */
    onActionClick?: () => void;
}

const CareerPositionPendingOfferScreen = ({ children, onActionClick }: ICareerPositionScreenProps<{}>) => {
    const { t } = useTranslation();
    return (
        <>
            <IssueStateView title={t('careers.position.pendingOfferTitle')}
                description={t('careers.position.pendingOfferContent')}
                button={!!onActionClick ? {
                    text: t('careers.position.pendingOfferBtn'),
                    onActionClick,
                } : undefined} />
            {children}
        </>
    )
}

const CareerPositionNotFoundScreen = ({ children, onActionClick }: ICareerPositionScreenProps<{}>) => {
    const { t } = useTranslation();
    return (
        <>
            <IssueStateView title={t('careers.position.notFoundTitle')}
                description={t('careers.position.notFoundContent')}
                button={!!onActionClick ? {
                    text: t('careers.position.notFoundBtn'),
                    onActionClick,
                } : undefined} />
            {children}
        </>
    )
}

interface ICandidateApplicationProps {
    /** The position id the user is applying to, 
     * if not provided it mean the position is not available anymore therefore user will just drop its CV for future opportunities */
    positionId?: string;
}
/** Container which takes care of taking the user information for a position */
export const CandidateApplication = ({ positionId }: ICandidateApplicationProps) => {
    const { t } = useTranslation();
    const [nameField, setNameField] =
        useState<Pick<TextFieldProps, 'value' | 'error' | 'helperText'>>({ value: '' });
    const [contactField, setContactField] =
        useState<Pick<TextFieldProps, 'value' | 'error' | 'helperText'>>({ value: '' });
    const [linkField, setLinkField] =
        useState<Pick<TextFieldProps, 'value' | 'error' | 'helperText'>>({ value: '' });

    const [open, setOpen] = useState(false);

    const handleSend = () => {
        const name = nameField.value as string;
        const email = contactField.value as string;
        const link = linkField.value as string;

        if (!name) {
            return setNameField(c => ({ ...c, error: true, helperText: t('error.requiredFieldEmpty') }))
        }

        if (!email) {
            return setContactField(c => ({ ...c, error: true, helperText: t('error.requiredFieldEmpty') }))
        }

        if (!link) {
            return setLinkField(l => ({ ...l, error: true, helperText: t('error.requiredFieldEmpty') }));
        }

        if (!isEmailValid(email)) {
            return setContactField(c => ({ ...c, error: true, helperText: t('error.invalidEmail') }))
        }

        //send somewhere...
        analytics.logEvent("generate_lead", { page: "career-position", name, email, link });
        addCareerApplication(positionId || "common", { status: 'new', name, email, link });

        //clear inputs
        setNameField({ value: '' });
        setContactField({ value: '' });
        setLinkField({ value: '' });

        return setOpen(true);
    }

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    return (
        <Paper sx={{ mx: 5, my: 3, p: 2 }} elevation={2}>
            <Stack direction={'column'}
                justifyContent={'center'}
                alignItems={'center'}
                spacing={1}>
                <Typography variant='h5' textAlign={'center'} fontWeight={'bold'}>{t(positionId ? 'careers.application.title' : 'careers.application.missedTitle')}</Typography>
                <Typography variant='body1' textAlign={'center'}>{t(positionId ? 'careers.application.content' : 'careers.application.missedContent')}</Typography>
                <TextField label={t('careers.application.nameInputLabel')}
                    placeholder={t('careers.application.nameInputLabel')}
                    type={'text'}
                    variant={'outlined'}
                    fullWidth
                    onChange={(e) => setNameField({ value: e.target.value })}
                    {...nameField} />
                <TextField label={t('careers.application.contactInputLabel')}
                    placeholder={t('careers.application.contactInputPlaceholder')}
                    type={'email'}
                    variant={'outlined'}
                    fullWidth
                    onChange={(e) => setContactField({ value: e.target.value })}
                    {...contactField} />
                <TextField label={t('careers.application.linkInputLabel')}
                    placeholder={t('careers.application.linkInputPlaceholder')}
                    type={'link'}
                    variant={'outlined'}
                    fullWidth
                    onChange={(e) => setLinkField({ value: e.target.value })}
                    {...linkField} />
                <Button variant='contained' onClick={handleSend}>{t(positionId ? 'careers.application.sendBtn' : 'careers.application.missedSendBtn')}</Button>
            </Stack>
            <Snackbar open={open}
                autoHideDuration={5000}
                onClose={handleClose}
                TransitionComponent={(props) => <Slide {...props} direction="up" />}>
                <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
                    {t('careers.application.successSendMessage')}
                </Alert>
            </Snackbar>
        </Paper>
    );
}

/** The display of a job listing */
export const CareerPositionScreen = ({
    title,
    subTitle,
    description,
    location,
    isRemote = true,
    salary,
    numberPosition,
    jobType,
    keyResponsibilities,
    keyKnowledge,
    keySkills,
    benefits,
    applicationDeadline,
    travelRequirement,
    process,
    children,
}: ICareerPositionScreenProps<{
    title: string,
    subTitle: string,
    description: string,
    location: string,
    isRemote?: boolean,
    salary: string,
    numberPosition: string;
    jobType: string,
    keyResponsibilities?: string[],
    keyKnowledge?: string[];
    keySkills?: string[];
    benefits: string[];
    applicationDeadline: string;
    travelRequirement?: string;
    process?: string[]
}>) => {
    const { t } = useTranslation();
    const applicationViewRef = useRef<HTMLDivElement>();
    const handleApplyClick = () => {
        //scroll to the application section which is in the children
        applicationViewRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    const renderPoint = (text: string, icon: JSX.Element) => <Stack direction={'row'} justifyContent={'start'} alignItems={'center'} spacing={1}>{icon}<Typography>{text}</Typography></Stack>

    return (
        <Container sx={{ p: 2 }}>
            <Stack direction={'column'} spacing={1}>
                <Typography variant="h2">{title}</Typography>
                <Typography variant="h3">{subTitle}</Typography>
                {!!children && <Button onClick={handleApplyClick}>{t('careers.application.sendBtn')}</Button>}
                <Divider />
                <Stack direction={'row'} spacing={1}>{renderPoint(location, <LocationCity />)}
                    {isRemote && <><Divider orientation="vertical" flexItem /><Typography>{t('careers.filters.remoteControlLabel')}</Typography></>}</Stack>
                {!!numberPosition && +numberPosition > 1 && renderPoint(numberPosition.toString(), <People />)}
                {renderPoint(jobType, <BusinessCenter />)}
                {renderPoint(salary, <Money />)}
                {!!applicationDeadline && renderPoint(applicationDeadline.toString(), <LockClock />)}
                <Divider />
                <Typography>{description}</Typography>
                {
                    !!keyResponsibilities?.length &&
                    <>
                        <Typography variant="h4">{t('careers.position.keyResponsibilitiesTitle')}</Typography>
                        <ul>
                            {keyResponsibilities.map((k, index) =>
                                <li key={'responsibility-' + index}>{k}</li>
                            )}
                        </ul>
                    </>
                }
                {
                    !!keyKnowledge?.length &&
                    <>
                        <Typography variant="h4">{t('careers.position.keyKnowledgeTitle')}</Typography>
                        <ul>
                            {keyKnowledge.map((k, index) =>
                                <li key={'knowledge-' + index}>{k}</li>
                            )}
                        </ul>
                    </>
                }
                {
                    !!keySkills?.length &&
                    <>
                        <Typography variant="h4">{t('careers.position.keySkillsTitle')}</Typography>
                        <ul>
                            {keySkills.map((k, index) =>
                                <li key={'skill-' + index}>{k}</li>
                            )}
                        </ul>
                    </>
                }
                {
                    !!benefits?.length &&
                    <>
                        <Typography variant="h4">{t('careers.position.keyBenefitsTitle')}</Typography>
                        <ul>
                            {benefits.map((k, index) =>
                                <li key={'benefit-' + index}>{k}</li>
                            )}
                        </ul>
                        <Divider />
                    </>
                }
                {
                    !!travelRequirement &&
                    <>
                        <Typography variant="h4">{t('careers.position.travelRequirementsTitle')}</Typography>
                        <Typography>{travelRequirement}</Typography>
                        <Divider />
                    </>
                }
                {
                    !!process?.length &&
                    <>
                        <Typography variant="h4">{t('careers.position.interviewProcessTitle')}</Typography><ul>
                            {process.map((k, index) =>
                                <li key={'benefit-' + index}>{k}</li>
                            )}
                        </ul>
                        <Divider />
                    </>
                }
                <Box ref={applicationViewRef}>
                    {children}
                </Box>
            </Stack>
        </Container>
    )
}